import { defineComponent as _defineComponent } from 'vue'
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from "vue";
  import {
    getDeviceZoomCenter,
    getCameraScroll,
    getMinimumCoverZoom,
  } from "@/helpers/masterplan/zoomViewport";
  import type {
    EstateLabelMetadata,
    StageLabelMetadata,
    ZoomCenter,
    ZoomLevels,
  } from "@/types/InteractiveMasterplanInterface";

  import { getBoundaryLabelCenter } from "@/helpers/masterplan/boundaryLabelCenter";

  type ZoomView = "estateBoundary" | "stages" | "lots";
  type DeviceMode = "desktop" | "mobile" | "landscape";
  type PreviewStage = {
    stageId: string;
    boundaryPath?: string;
    boundaryPoints?: Array<{ x: number; y: number }>;
  };
  type PreviewLot = {
    lotId: string;
    stageId?: string;
    shapePath?: string;
    polygonPoints?: Array<{ x: number; y: number }>;
    boundingBox?: { x: number; y: number; width: number; height: number };
  };

  const MIN_ZOOM = 1;
  const ZOOM_GAP = 0.1;

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ZoomLevelsEditor',
  props: {
    svgFile: {},
    stages: {},
    estateBoundaryPath: {},
    initialZoomLevels: {},
    initialPreviewState: {},
    initialEstateZoomCenter: {},
    initialStageZoomCenter: {},
    availableStages: {},
    lots: {},
    availableLots: {},
    stageLabelMetadata: {},
    estateLabelMetadata: {},
    estateName: {},
    landSubdivision: { type: Boolean }
  },
  emits: ["complete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emit = __emit;

  const views: Array<{ key: ZoomView; title: string; description: string }> = [
    {
      key: "estateBoundary",
      title: "Overall project",
      description: "The first view customers see",
    },
    {
      key: "stages",
      title: "Stages view",
      description: "After opening the project",
    },
    {
      key: "lots",
      title: "Individual stage",
      description: "After selecting a stage",
    },
  ];

  const activeView = ref<ZoomView>(
    props.initialPreviewState?.activeView ?? "estateBoundary"
  );

  const desktopZoomLevels = ref<ZoomLevels>({
    estateBoundary: props.initialZoomLevels?.estateBoundary ?? 1,
    stages: props.initialZoomLevels?.stages ?? 2,
    lots: props.initialZoomLevels?.lots ?? 3,
  });

  const mobileZoomLevels = ref({
    estateBoundary:
      props.initialZoomLevels?.mobile?.estateBoundary ??
      desktopZoomLevels.value.estateBoundary,
    stages:
      props.initialZoomLevels?.mobile?.stages ?? desktopZoomLevels.value.stages,
    lots: props.initialZoomLevels?.mobile?.lots ?? desktopZoomLevels.value.lots,
  });

  const landscapeZoomLevels = ref({
    ...mobileZoomLevels.value,
    ...props.initialZoomLevels?.mobileLandscape,
  });

  const zoomLevels = computed(() =>
    deviceMode.value === "desktop"
      ? desktopZoomLevels.value
      : deviceMode.value === "landscape"
      ? landscapeZoomLevels.value
      : mobileZoomLevels.value
  );

  const svgText = ref("");
  const svgUrl = ref("");
  const previewFrameElement = ref<HTMLElement | null>(null);
  const frameWidth = ref(1100);

  const screenWidth = ref(
    typeof window === "undefined" ? 1440 : window.innerWidth
  );

  const screenHeight = ref(
    typeof window === "undefined" ? 900 : window.innerHeight
  );

  const deviceMode = ref<DeviceMode>(
    props.initialPreviewState?.deviceMode ?? "desktop"
  );

  const mobileExpanded = ref(
    props.initialPreviewState?.mobileExpanded ?? false
  );

  const deviceControlsElement = ref<HTMLElement | null>(null);

  let layoutScrollFrame: number | null = null;
  let layoutChangeId = 0;
  const changePreviewLayout = async (
    mode: DeviceMode,
    expanded = mobileExpanded.value
  ) => {
    const changeId = ++layoutChangeId;

    if (layoutScrollFrame !== null) cancelAnimationFrame(layoutScrollFrame);

    const controls = deviceControlsElement.value;
    const originalTop = controls?.getBoundingClientRect().top;
    const scrollParents: HTMLElement[] = [];

    for (
      let parent = controls?.parentElement;
      parent;
      parent = parent.parentElement
    ) {
      if (/(auto|scroll|overlay)/.test(getComputedStyle(parent).overflowY)) {
        scrollParents.push(parent);
      }
    }

    const page = document.scrollingElement as HTMLElement | null;

    if (page && !scrollParents.includes(page)) scrollParents.push(page);

    const restorePosition = () => {
      if (!controls?.isConnected || originalTop === undefined) return;

      for (const parent of scrollParents) {
        const difference = controls.getBoundingClientRect().top - originalTop;

        if (Math.abs(difference) < 0.5) break;

        const previousBehavior = parent.style.scrollBehavior;

        parent.style.scrollBehavior = "auto";
        parent.scrollTop += difference;
        parent.style.scrollBehavior = previousBehavior;
      }
    };

    deviceMode.value = mode;
    mobileExpanded.value = expanded;
    await nextTick();

    if (changeId !== layoutChangeId) return;

    if (previewFrameElement.value)
      frameWidth.value = previewFrameElement.value.clientWidth;

    await nextTick();

    if (changeId !== layoutChangeId) return;

    restorePosition();
    layoutScrollFrame = requestAnimationFrame(() => {
      restorePosition();
      layoutScrollFrame = requestAnimationFrame(() => {
        restorePosition();
        layoutScrollFrame = null;
      });
    });
  };

  const selectedStageId = ref(
    props.initialPreviewState?.selectedStageId || props.stages[0]?.stageId || ""
  );

  const initialCenterSettings = {
    zoomLevels: props.initialZoomLevels,
    estateZoomCenter: props.initialEstateZoomCenter,
    stageZoomCenter: props.initialStageZoomCenter,
  };

  const createCenters = (
    mode: DeviceMode
  ): Record<ZoomView, ZoomCenter | null> => {
    const copyCenter = (key: "estateZoomCenter" | "stageZoomCenter") => {
      const center = getDeviceZoomCenter(
        initialCenterSettings,
        key,
        mode === "mobile",
        mode === "landscape"
      );

      return center ? { ...center } : null;
    };

    return {
      estateBoundary: copyCenter("estateZoomCenter"),
      stages: copyCenter("stageZoomCenter"),
      lots: null,
    };
  };

  const deviceCenters = reactive({
    desktop: createCenters("desktop"),
    mobile: createCenters("mobile"),
    landscape: createCenters("landscape"),
  });

  const centers = computed(() => deviceCenters[deviceMode.value]);

  const isDragging = ref(false);

  let activePointerId: number | null = null;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragStartCenter: ZoomCenter = { x: 0, y: 0 };
  let previewResizeObserver: ResizeObserver | null = null;

  const parseBounds = (path: string | undefined) => {
    if (!path) return null;

    const values = (path.match(/-?[\d.]+(?:e[+-]?\d+)?/gi) || []).map(Number);

    const xs: number[] = [];

    const ys: number[] = [];

    for (let index = 0; index + 1 < values.length; index += 2) {
      xs.push(values[index]);
      ys.push(values[index + 1]);
    }

    if (!xs.length) return null;

    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  };

  const viewBox = computed(() => {
    const fallback = { x: 0, y: 0, width: 100, height: 100 };
    const match = svgText.value.match(/<svg[^>]*\bviewBox=["']([^"']+)["']/i);
    const values = match?.[1].trim().split(/[ ,]+/).map(Number);

    if (
      values &&
      values.length === 4 &&
      values.every(Number.isFinite) &&
      values[2] > 0 &&
      values[3] > 0
    ) {
      return {
        x: values[0],
        y: values[1],
        width: values[2],
        height: values[3],
      };
    }

    const width = svgText.value.match(/<svg[^>]*\bwidth=["']([^"']+)["']/i);
    const height = svgText.value.match(/<svg[^>]*\bheight=["']([^"']+)["']/i);

    const parsedWidth = Number.parseFloat(width?.[1] || "");
    const parsedHeight = Number.parseFloat(height?.[1] || "");

    if (parsedWidth > 0 && parsedHeight > 0) {
      return { x: 0, y: 0, width: parsedWidth, height: parsedHeight };
    }

    return fallback;
  });

  const viewBoxString = computed(() => {
    const vb = viewBox.value;

    return [vb.x, vb.y, vb.width, vb.height].join(" ");
  });

  const stages = computed(() =>
    props.stages.filter((stage) => Boolean(stage.boundaryPath))
  );
  const combinedStageBounds = computed(() => {
    const bounds = stages.value
      .map((stage) => parseBounds(stage.boundaryPath))
      .filter(Boolean) as Array<NonNullable<ReturnType<typeof parseBounds>>>;

    if (!bounds.length) return null;

    return {
      minX: Math.min(...bounds.map((item) => item.minX)),
      maxX: Math.max(...bounds.map((item) => item.maxX)),
      minY: Math.min(...bounds.map((item) => item.minY)),
      maxY: Math.max(...bounds.map((item) => item.maxY)),
    };
  });

  const getDefaultCenter = (view: ZoomView): ZoomCenter => {
    const vb = viewBox.value;
    let bounds = null;

    if (view === "stages") {
      bounds =
        combinedStageBounds.value || parseBounds(props.estateBoundaryPath);
    } else if (view === "lots") {
      bounds = parseBounds(
        stages.value.find((stage) => stage.stageId === selectedStageId.value)
          ?.boundaryPath
      );
    }

    return {
      x: bounds ? (bounds.minX + bounds.maxX) / 2 : vb.x + vb.width / 2,
      y: bounds ? (bounds.minY + bounds.maxY) / 2 : vb.y + vb.height / 2,
    };
  };

  const getPreviewMetrics = (mode: DeviceMode, expanded: boolean) => {
    const availableWidth = Math.max(1, Math.min(1100, frameWidth.value));

    if (mode === "desktop") {
      const buyerWidth = Math.max(1024, screenWidth.value);
      const buyerHeight = Math.max(500, screenHeight.value) - 64;
      const sidebarWidth = props.landSubdivision
        ? Math.min(384, Math.max(320, buyerWidth * 0.25))
        : Math.min(553, Math.max(320, buyerWidth * 0.3));
      const scale = availableWidth / buyerWidth;

      return {
        frameWidth: availableWidth,
        mapWidth: (buyerWidth - sidebarWidth) * scale,
        mapHeight: buyerHeight * scale,
        panelWidth: sidebarWidth * scale,
        panelHeight: buyerHeight * scale,
      };
    }

    const phoneWidth = mode === "landscape" ? 847 : 402;
    const phoneHeight = mode === "landscape" ? 402 : 847;

    const width = Math.min(phoneWidth, availableWidth);

    const scale = width / phoneWidth;

    const mapHeight = expanded ? phoneHeight : mode === "landscape" ? 286 : 288;

    return {
      frameWidth: width,
      mapWidth: width,
      mapHeight: mapHeight * scale,
      panelWidth: width,
      panelHeight: expanded ? 0 : (phoneHeight - mapHeight) * scale,
    };
  };

  const previewMetrics = computed(() =>
    getPreviewMetrics(deviceMode.value, mobileExpanded.value)
  );
  const getPreviewBaseWidth = (mode: DeviceMode, expanded: boolean) => {
    const metrics = getPreviewMetrics(mode, expanded);
    const vb = viewBox.value;

    if (mode !== "desktop" && expanded && vb.height > 0) {
      return Math.max(
        metrics.mapWidth,
        metrics.mapHeight * (vb.width / vb.height)
      );
    }

    return metrics.mapWidth;
  };
  const getCoverZoom = (mode: DeviceMode, expanded: boolean) => {
    const metrics = getPreviewMetrics(mode, expanded);
    const vb = viewBox.value;

    return getMinimumCoverZoom(
      metrics.mapWidth,
      metrics.mapHeight,
      vb.width,
      vb.height,
      getPreviewBaseWidth(mode, expanded)
    );
  };

  const minimumCoverZoom = computed(() =>
    getCoverZoom(deviceMode.value, mobileExpanded.value)
  );
  const getEffectiveZoom = (view: ZoomView) =>
    Math.max(
      minimumCoverZoom.value,
      Number(zoomLevels.value[view]) || MIN_ZOOM
    );
  const displayedZoom = computed({
    get: () =>
      Number(
        (getEffectiveZoom(activeView.value) / minimumCoverZoom.value).toFixed(2)
      ),
    set: (value: number) => {
      zoomLevels.value[activeView.value] =
        Math.min(zoomControlMax.value, Math.max(1, value)) *
        minimumCoverZoom.value;
    },
  });

  const zoomControlMax = computed(() =>
    Math.max(10, Math.ceil(displayedZoom.value) || 10)
  );

  const invalidDeviceNames = computed(() =>
    [
      { name: "Desktop", levels: desktopZoomLevels.value },
      { name: "Mobile portrait", levels: mobileZoomLevels.value },
      { name: "Mobile landscape", levels: landscapeZoomLevels.value },
    ]
      .filter(
        ({ levels }) =>
          !(
            [levels.estateBoundary, levels.stages, levels.lots].every(
              Number.isFinite
            ) &&
            levels.estateBoundary >= MIN_ZOOM - 0.001 &&
            levels.stages >= levels.estateBoundary + ZOOM_GAP - 0.001 &&
            levels.lots >= levels.stages + ZOOM_GAP - 0.001
          )
      )
      .map(({ name }) => name)
  );

  const hasValidZoomOrder = computed(
    () => invalidDeviceNames.value.length === 0
  );

  const zoomValidationMessage = computed(
    () =>
      `${invalidDeviceNames.value.join(
        ", "
      )}: increase the zoom from overall project to stages to individual stage.`
  );

  const activeViewLabel = computed(
    () => views.find((view) => view.key === activeView.value)?.title || "View"
  );

  const previewHelpText = computed(() =>
    activeView.value === "stages"
      ? "Drag the estate to set the stages-view centre"
      : activeView.value === "lots"
      ? "Drag to inspect · The selected stage is centred automatically"
      : "Drag the map to set its starting position"
  );

  const previewFrameStyle = computed(() => ({
    height:
      String(
        previewMetrics.value.mapHeight +
          (deviceMode.value !== "desktop"
            ? previewMetrics.value.panelHeight
            : 0)
      ) + "px",
  }));

  const previewViewportStyle = computed(() => ({
    width: String(previewMetrics.value.mapWidth) + "px",
    height: String(previewMetrics.value.mapHeight) + "px",
  }));

  const previewPanelStyle = computed(() => ({
    width: String(previewMetrics.value.panelWidth) + "px",
    height: String(previewMetrics.value.panelHeight) + "px",
  }));

  const getContentSize = (view: ZoomView) => {
    const width =
      getPreviewBaseWidth(deviceMode.value, mobileExpanded.value) *
      getEffectiveZoom(view);

    return {
      width,
      height: width * (viewBox.value.height / viewBox.value.width),
    };
  };

  const contentSize = computed(() => getContentSize(activeView.value));

  const clampCenter = (center: ZoomCenter, view: ZoomView): ZoomCenter => {
    const vb = viewBox.value;

    const size = getContentSize(view);

    const halfVisibleWidth =
      (previewMetrics.value.mapWidth / (2 * size.width)) * vb.width;

    const halfVisibleHeight =
      (previewMetrics.value.mapHeight / (2 * size.height)) * vb.height;

    return {
      x: Math.min(
        vb.x + vb.width - halfVisibleWidth,
        Math.max(vb.x + halfVisibleWidth, center.x)
      ),
      y: Math.min(
        vb.y + vb.height - halfVisibleHeight,
        Math.max(vb.y + halfVisibleHeight, center.y)
      ),
    };
  };

  const currentCenter = computed(() =>
    clampCenter(
      centers.value[activeView.value] || getDefaultCenter(activeView.value),
      activeView.value
    )
  );

  const previewImageStyle = computed(() => {
    const scroll = getCameraScroll(
      centers.value[activeView.value] || getDefaultCenter(activeView.value),
      viewBox.value,
      contentSize.value,
      {
        width: previewMetrics.value.mapWidth,
        height: previewMetrics.value.mapHeight,
      }
    );

    return {
      width: contentSize.value.width + "px",
      height: contentSize.value.height + "px",
      left: -scroll.left + "px",
      top: -scroll.top + "px",
    };
  });

  const getLabelTransform = (
    center: ZoomCenter,
    scale: number,
    rotation: number
  ) => {
    if (scale === 1 && rotation === 0) return "";

    return [
      "translate(" + center.x + " " + center.y + ")",
      "scale(" + scale + ")",
      "rotate(" + rotation + ")",
      "translate(" + -center.x + " " + -center.y + ")",
    ].join(" ");
  };

  const getStageTitle = (stageId: string, index: number) => {
    const stage = props.availableStages?.find(
      (item) => String(item._id) === String(stageId)
    );

    return "Stage " + String(stage?.title || index + 1);
  };

  const getStageLabelMetadata = (stageId: string) =>
    props.stageLabelMetadata?.find(
      (item) => String(item.stageId) === String(stageId)
    );

  const getStageLabelCenter = (stage: PreviewStage) => {
    const centroid = getBoundaryLabelCenter(
      stage.boundaryPath,
      stage.boundaryPoints
    );
    const metadata = getStageLabelMetadata(stage.stageId);

    return {
      x: (centroid?.x || 0) + (metadata?.offsetX || 0),
      y: (centroid?.y || 0) + (metadata?.offsetY || 0),
    };
  };

  const getStageLabelTransform = (stage: PreviewStage) => {
    const metadata = getStageLabelMetadata(stage.stageId);

    return getLabelTransform(
      getStageLabelCenter(stage),
      metadata?.scale ?? 1,
      metadata?.rotation ?? 0
    );
  };

  const isStageLabelVisible = (stageId: string) =>
    getStageLabelMetadata(stageId)?.displayed !== false;

  const estateLabelCenter = computed(() => {
    const centroid = getBoundaryLabelCenter(props.estateBoundaryPath);
    const vb = viewBox.value;

    return {
      x:
        (centroid?.x ?? vb.x + vb.width / 2) +
        (props.estateLabelMetadata?.offsetX || 0),
      y:
        (centroid?.y ?? vb.y + vb.height / 2) +
        (props.estateLabelMetadata?.offsetY || 0),
    };
  });

  const estateLabelTransform = computed(() =>
    getLabelTransform(
      estateLabelCenter.value,
      props.estateLabelMetadata?.scale ?? 3,
      props.estateLabelMetadata?.rotation ?? 0
    )
  );

  const estateLogoWidth = computed(
    () =>
      Math.round(Math.min(viewBox.value.width, viewBox.value.height) / 40) * 25
  );

  const estateLogoHeight = computed(() =>
    Math.round(estateLogoWidth.value * 0.4)
  );

  const previewLots = computed(() => props.lots || []);

  const visibleLots = computed(() =>
    previewLots.value.filter(
      (lot) => String(lot.stageId) === String(selectedStageId.value)
    )
  );

  const getPolygonPoints = (lot: PreviewLot) =>
    (lot.polygonPoints || [])
      .map((point) => String(point.x) + "," + String(point.y))
      .join(" ");

  const getLotCenter = (lot: PreviewLot) => {
    if (lot.boundingBox) {
      return {
        x: lot.boundingBox.x + lot.boundingBox.width / 2,
        y: lot.boundingBox.y + lot.boundingBox.height / 2,
      };
    }

    if (lot.polygonPoints?.length) {
      return {
        x:
          lot.polygonPoints.reduce((sum, point) => sum + point.x, 0) /
          lot.polygonPoints.length,
        y:
          lot.polygonPoints.reduce((sum, point) => sum + point.y, 0) /
          lot.polygonPoints.length,
      };
    }

    return null;
  };

  const getLotTitle = (lotId: string) =>
    props.availableLots?.find((lot) => String(lot.lotId) === String(lotId))
      ?.lotTitle || "";
  const previewStageFontSize = computed(() =>
    Math.round(Math.min(viewBox.value.width, viewBox.value.height) / 20)
  );

  const previewEstateFontSize = computed(
    () =>
      Math.round(Math.min(viewBox.value.width, viewBox.value.height) / 40) * 3
  );

  const previewLotFontSize = computed(() =>
    Math.round(Math.min(viewBox.value.width, viewBox.value.height) / 40)
  );

  const loadSvg = async () => {
    svgText.value = await props.svgFile.text();

    if (svgUrl.value) URL.revokeObjectURL(svgUrl.value);

    svgUrl.value = URL.createObjectURL(props.svgFile);
  };

  const updateZoomInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const value = Number(input.value);

    displayedZoom.value = Number.isFinite(value) ? value : 1;
    input.value = String(displayedZoom.value);
  };

  const changeZoom = (amount: number) => {
    const next = Math.min(
      zoomControlMax.value,
      Math.max(1, displayedZoom.value + amount)
    );

    displayedZoom.value = Math.round(next * 100) / 100;
  };

  const startPan = (event: PointerEvent) => {
    if (!svgUrl.value || (event.pointerType === "mouse" && event.button !== 0))
      return;

    const target = event.currentTarget as HTMLElement;

    target.setPointerCapture(event.pointerId);
    activePointerId = event.pointerId;
    isDragging.value = true;
    dragStartX = event.clientX;
    dragStartY = event.clientY;
    dragStartCenter = { ...currentCenter.value };
  };

  const pan = (event: PointerEvent) => {
    if (!isDragging.value || event.pointerId !== activePointerId) return;

    const vb = viewBox.value;

    centers.value[activeView.value] = clampCenter(
      {
        x:
          dragStartCenter.x -
          ((event.clientX - dragStartX) / contentSize.value.width) * vb.width,
        y:
          dragStartCenter.y -
          ((event.clientY - dragStartY) / contentSize.value.height) * vb.height,
      },
      activeView.value
    );
  };

  const stopPan = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;

    const target = event.currentTarget as HTMLElement;

    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }

    activePointerId = null;
    isDragging.value = false;
  };

  const zoomWithWheel = (event: WheelEvent) => {
    changeZoom(event.deltaY < 0 ? 0.1 : -0.1);
  };

  const resetPosition = () => {
    centers.value[activeView.value] = null;
  };

  const complete = () => {
    if (!hasValidZoomOrder.value) return;

    emit("complete", {
      previewState: {
        deviceMode: deviceMode.value,
        activeView: activeView.value,
        mobileExpanded: mobileExpanded.value,
        selectedStageId: selectedStageId.value,
      },
      zoomLevels: {
        estateBoundary: Number(desktopZoomLevels.value.estateBoundary),
        stages: Number(desktopZoomLevels.value.stages),
        lots: Number(desktopZoomLevels.value.lots),
        mobile: {
          ...mobileZoomLevels.value,
          estateZoomCenter: deviceCenters.mobile.estateBoundary,
          stageZoomCenter: deviceCenters.mobile.stages,
        },
        mobileLandscape: {
          ...landscapeZoomLevels.value,
          estateZoomCenter: deviceCenters.landscape.estateBoundary,
          stageZoomCenter: deviceCenters.landscape.stages,
        },
      },
      ...(deviceCenters.desktop.estateBoundary
        ? { estateZoomCenter: { ...deviceCenters.desktop.estateBoundary } }
        : {}),
      ...(deviceCenters.desktop.stages
        ? { stageZoomCenter: { ...deviceCenters.desktop.stages } }
        : {}),
    });
  };

  const updateScreenSize = () => {
    screenWidth.value = window.innerWidth;
    screenHeight.value = window.innerHeight;
  };

  watch(() => props.svgFile, loadSvg, { immediate: true });

  watch(
    () => props.stages,
    () => {
      if (
        !stages.value.some((stage) => stage.stageId === selectedStageId.value)
      ) {
        selectedStageId.value = stages.value[0]?.stageId || "";
      }
    },
    { immediate: true }
  );

  watch(selectedStageId, () => {
    Object.values(deviceCenters).forEach((centers) => {
      centers.lots = null;
    });
  });

  onMounted(() => {
    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);

    if (previewFrameElement.value) {
      previewResizeObserver = new ResizeObserver(() => {
        frameWidth.value = previewFrameElement.value?.clientWidth || 1100;
      });
      previewResizeObserver.observe(previewFrameElement.value);
      frameWidth.value = previewFrameElement.value.clientWidth;
    }
  });

  onBeforeUnmount(() => {
    layoutChangeId++;

    if (layoutScrollFrame !== null) cancelAnimationFrame(layoutScrollFrame);

    if (svgUrl.value) URL.revokeObjectURL(svgUrl.value);

    previewResizeObserver?.disconnect();
    window.removeEventListener("resize", updateScreenSize);
  });

const __returned__ = { MIN_ZOOM, ZOOM_GAP, props, emit, views, activeView, desktopZoomLevels, mobileZoomLevels, landscapeZoomLevels, zoomLevels, svgText, svgUrl, previewFrameElement, frameWidth, screenWidth, screenHeight, deviceMode, mobileExpanded, deviceControlsElement, get layoutScrollFrame() { return layoutScrollFrame }, set layoutScrollFrame(v) { layoutScrollFrame = v }, get layoutChangeId() { return layoutChangeId }, set layoutChangeId(v) { layoutChangeId = v }, changePreviewLayout, selectedStageId, initialCenterSettings, createCenters, deviceCenters, centers, isDragging, get activePointerId() { return activePointerId }, set activePointerId(v) { activePointerId = v }, get dragStartX() { return dragStartX }, set dragStartX(v) { dragStartX = v }, get dragStartY() { return dragStartY }, set dragStartY(v) { dragStartY = v }, get dragStartCenter() { return dragStartCenter }, set dragStartCenter(v) { dragStartCenter = v }, get previewResizeObserver() { return previewResizeObserver }, set previewResizeObserver(v) { previewResizeObserver = v }, parseBounds, viewBox, viewBoxString, stages, combinedStageBounds, getDefaultCenter, getPreviewMetrics, previewMetrics, getPreviewBaseWidth, getCoverZoom, minimumCoverZoom, getEffectiveZoom, displayedZoom, zoomControlMax, invalidDeviceNames, hasValidZoomOrder, zoomValidationMessage, activeViewLabel, previewHelpText, previewFrameStyle, previewViewportStyle, previewPanelStyle, getContentSize, contentSize, clampCenter, currentCenter, previewImageStyle, getLabelTransform, getStageTitle, getStageLabelMetadata, getStageLabelCenter, getStageLabelTransform, isStageLabelVisible, estateLabelCenter, estateLabelTransform, estateLogoWidth, estateLogoHeight, previewLots, visibleLots, getPolygonPoints, getLotCenter, getLotTitle, previewStageFontSize, previewEstateFontSize, previewLotFontSize, loadSvg, updateZoomInput, changeZoom, startPan, pan, stopPan, zoomWithWheel, resetPosition, complete, updateScreenSize, computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch, get getDeviceZoomCenter() { return getDeviceZoomCenter }, get getCameraScroll() { return getCameraScroll }, get getMinimumCoverZoom() { return getMinimumCoverZoom }, get getBoundaryLabelCenter() { return getBoundaryLabelCenter } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})